import React from 'react';

import { Btn, Bx, ExtendedListItem, IconAva, IconBtn, Input, InputStartAdornment, Typo } from '@curry-group/mui-curcuma';
import { faPlus, faSearch, faTimes, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface IAddSearchProps {
  maxHeight?: number;
  size?: 'large' | 'medium';
  placedInSidebar?: boolean;

  addButtonCaption?: string | React.ReactElement;
  searchEnabled?: boolean;
  searchActive?: boolean;
  searchInput?: string;
  searchPlaceholder?: string;
  addButtonClicked?: () => void;
  /**
   * Label shown where the add button would be, for lists that have nothing to
   * create. Without it the search icon would be the only element and sit on the
   * left. Hidden while the search input is open.
   */
  caption?: React.ReactNode;
  setSearchActive?: (active: boolean) => void;
  searchInputChange?: (value: string) => void;
  isMobile?: boolean;
  addButtonIcon?: IconDefinition;
  addButtonDisabled?: boolean;
}

export const AddSearch: React.FC<IAddSearchProps> = props => {
  return (
    <ExtendedListItem key={'list_control_add_search'}
      height={props.maxHeight}
      maxHeight={props.maxHeight}
    >
      <Bx
        display="flex"
        mr={props.placedInSidebar ? (props.isMobile ? 2 : 3) : 0}
        ml={props.searchActive && props.placedInSidebar ? (props.isMobile ? 2 : 0) : undefined}
        alignItems="center"
        height="100%"
      >
        <Bx display="flex" alignItems="center" justifyContent="space-between" width="100%">
          {/*
            Only render the add button when there is something to add. Without a
            handler it used to paint a "+" that does nothing — visible in every
            list that enables search but no creation.
          */}
          {!!props.addButtonClicked && <Bx ml={!props.size || props.size === 'medium' ? (props.isMobile ? 2 : 3) : 0} mr={'auto'}>
            <Btn
              onClick={() => props.addButtonClicked && props.addButtonClicked()}
              style={{padding:'4px'}}
              disabled={props.addButtonDisabled}
              startIcon={
                <IconAva
                  style={{
                    width: props.size === 'large' ? '64px' : undefined,
                    height: props.size === 'large' ? '64px' : undefined,
                  }}
                  size={props.size || 'medium'}
                  color='secondary'
                  variant='circular'
                  inverted={true}
                  children={<FontAwesomeIcon icon={props.addButtonIcon ? props.addButtonIcon : faPlus} />}
                />}
            >
              {!props.searchActive && (<React.Fragment>
                {typeof props.addButtonCaption === 'string' || typeof props.addButtonCaption === undefined ? (
                  <Typo color="textPrimary" fontWeight={500} variant="body2" component="span">
                    {props.addButtonCaption || 'Erstellen'}
                  </Typo>
                ) : (
                  props.addButtonCaption
                )}
              </React.Fragment>)}
            </Btn>
          </Bx>}
          {/*
            No add button: the headline fills that slot while the search is closed.
            Once it opens, a search icon takes the slot instead — same size as the
            create button, so the row keeps its anchor and the input starts where it
            would with a button present. Decorative only: the clickable search stays
            on the trailing edge.
          */}
          {!props.addButtonClicked && props.searchActive && (
            <Bx
              ml={!props.size || props.size === 'medium' ? (props.isMobile ? 2 : 3) : 0}
              // Keeps the input off the icon, the same gap the create button leaves.
              mr={props.isMobile ? 1 : 2}
              display="flex"
              alignItems="center"
            >
              <IconAva
                style={{
                  width: props.size === 'large' ? '64px' : undefined,
                  height: props.size === 'large' ? '64px' : undefined,
                }}
                size={props.size || 'medium'}
                color="secondary"
                variant="circular"
                inverted={true}
                children={<FontAwesomeIcon icon={faSearch} />}
              />
            </Bx>
          )}
          {!props.addButtonClicked && !props.searchActive && !!props.caption && (
            <Bx ml={!props.size || props.size === 'medium' ? (props.isMobile ? 2 : 3) : 0} mr={'auto'}>
              <Typo color="textPrimary" fontWeight={500} variant="body1" component="span" style={{ fontSize: 18 }}>
                {props.caption}
              </Typo>
            </Bx>
          )}
          {props.searchActive && (
            <Bx my={props.placedInSidebar ? -0.5 : 0} ml={'auto'} width="100%">
              <Input
                input={{
                  autoFocus: true,
                  startAdornment: props.searchEnabled && props.setSearchActive && (
                    <InputStartAdornment>
                      <IconBtn
                        size="small"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          props.searchInputChange?.('');
                          props.setSearchActive?.(!props.searchActive);
                        }}
                      >
                        <FontAwesomeIcon icon={props.searchActive ? faTimes : faSearch} />
                      </IconBtn>
                    </InputStartAdornment>
                  ),
                  placeholder: props.searchPlaceholder || 'Suche nach...',
                  value: props.searchInput,
                  onChange: e => {
                    props.searchInputChange?.(e.target.value);
                  }
                }}
              />
            </Bx>
          )}
          {!props.searchActive && props.searchEnabled && props.setSearchActive && (
            <IconBtn
              size="small"
              onClick={(e: any) => {
                e.stopPropagation();
                props.setSearchActive?.(true);
              }}
            >
              <FontAwesomeIcon icon={faSearch} />
            </IconBtn>
          )}
        </Bx>
      </Bx>
    </ExtendedListItem>
  );
};
